<template>
  <div v-if="visible" class="preview-overlay" @click="handleClose">
    <div class="preview-container" @click.stop>
      <!-- 关闭按钮 -->
      <button class="preview-close-btn" @click="handleClose">
        <Close />
      </button>
      
        <!-- Logo预览区域 -->
        <div class="preview-logo-area">
          <div class="preview-canvas">
            <div v-if="loading" class="preview-loading">
              <div class="loading-spinner"></div>
              <p>正在加载...</p>
            </div>
            <!-- 显示原始SVG（当选择0时） -->
            <div 
              v-else-if="currentIndex === 0 && originalSvgContent" 
              class="preview-svg-content"
              v-html="originalSvgContent"
            ></div>
            <!-- 显示当前选中的预览图（索引1+） -->
            <div 
              v-else-if="currentPreviewImage && currentIndex > 0" 
              class="preview-image-content"
            >
              <img :src="currentPreviewImage" :alt="`预览样式 ${currentIndex}`" />
            </div>
            <!-- 显示原始SVG作为默认 -->
            <div 
              v-else-if="originalSvgContent" 
              class="preview-svg-content"
              v-html="originalSvgContent"
            ></div>
            <!-- 显示Logo原图 -->
            <div 
              v-else-if="currentLogo?.image" 
              class="preview-image-content"
            >
              <img :src="currentLogo.image" :alt="currentLogo?.title" />
            </div>
            <div v-else class="preview-no-content">
              <p>暂无内容</p>
            </div>
          </div>
        </div>

      <!-- 预览图选择器 -->
      <div v-if="originalSvgContent || previewImages.length > 0" class="preview-examples-selector">
        <div class="examples-thumbnails">
          <!-- 原始SVG缩略图（第一个） -->
          <div 
            v-if="originalSvgContent"
            class="example-thumbnail original-svg-thumbnail"
            :class="{ active: currentIndex === 0 }"
            @click="selectExample(0)"
          >
            <div class="svg-thumbnail-content" v-html="originalSvgContent"></div>
            <div class="thumbnail-label">原始</div>
          </div>
          
          <!-- 分隔符 -->
          <div v-if="originalSvgContent && previewImages.length > 0" class="thumbnail-separator"></div>
          
          <!-- 场景图标签 -->
          <div v-if="previewImages.length > 0" class="scene-label">
            <span class="examples-label">场景图:</span>
          </div>
          
          <!-- 预览图缩略图 -->
          <div 
            v-for="(previewImage, index) in previewImages" 
            :key="`preview-${index}`"
            class="example-thumbnail"
            :class="{ active: currentIndex === index + 1 }"
            @click="selectExample(index + 1)"
          >
            <!-- 骨架屏占位符 -->
            <div 
              v-if="thumbnailLoadingStates[index]"
              class="skeleton-thumbnail-content"
            >
              <div class="skeleton-shimmer"></div>
            </div>
            <!-- 实际图片 -->
            <img 
              v-else-if="processedPreviewImages[index]"
              :src="processedPreviewImages[index]" 
              :alt="`预览样式 ${index + 1}`"
              @error="handleImageError"
            />
            <!-- 加载失败占位符 -->
            <div 
              v-else
              class="thumbnail-placeholder"
            >
              加载失败
            </div>
          </div>
        </div>
      </div>

      <!-- 底部按钮区域 -->
      <div class="preview-actions">
        <!-- 当前选中预览图时显示VIP下载按钮 -->
        <button 
          v-if="currentIndex > 0" 
          class="vip-download-btn" 
          @click="handleVipDownload"
        >
          <span class="vip-icon">👑</span>
          VIP专属下载
        </button>
        <!-- 当前选中原始Logo时显示普通下载按钮 -->
        <button 
          v-if="currentIndex === 0" 
          class="download-logo-btn" 
          @click="handleDownload"
        >
          下载LOGO
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { Close } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getExampleApi } from '@/api/index.js'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  logo: {
    type: Object,
    default: null
  },
  examples: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  userInfo: {
    type: Object,
    default: () => ({})
  }
})

// Emits
const emit = defineEmits([
  'close',
  'download',
  'load-examples',
  'show-vip-modal'
])

// 响应式数据
const currentIndex = ref(0)
const logoDetails = ref(null)
const previewImages = ref([])
const processedPreviewImages = ref([]) // 存储处理后的预览图
const isLoadingThumbnails = ref(false) // 缩略图加载状态
const thumbnailLoadingStates = ref([]) // 每个缩略图的加载状态

// 计算属性
const currentLogo = computed(() => props.logo)

// 判断是否为VIP用户
const isVip = computed(() => {
  return props.userInfo.level && props.userInfo.level.includes('VIP')
})

// 获取当前显示的预览图
const currentPreviewImage = computed(() => {
  // 索引0是原始SVG，索引1+是预览图
  if (currentIndex.value > 0 && processedPreviewImages.value.length > 0) {
    const previewIndex = currentIndex.value - 1
    if (processedPreviewImages.value[previewIndex]) {
      return processedPreviewImages.value[previewIndex]
    }
  }
  return null
})

// 获取原始SVG内容
const originalSvgContent = computed(() => {
  if (logoDetails.value && logoDetails.value.svg) {
    return logoDetails.value.svg
  }
  if (currentLogo.value && currentLogo.value.svgData) {
    return currentLogo.value.svgData
  }
  return null
})

// 方法
const handleClose = () => {
  emit('close')
  resetState()
}

const resetState = () => {
  currentIndex.value = 0
  logoDetails.value = null
  previewImages.value = []
  processedPreviewImages.value = []
  isLoadingThumbnails.value = false
  thumbnailLoadingStates.value = []
}

const selectExample = (index) => {
  currentIndex.value = index
}

const handleVipDownload = () => {
  // 判断是否为VIP用户
  if (!isVip.value) {
    // 不是VIP用户，弹出开通会员弹窗
    emit('show-vip-modal')
    return
  }
  
  // 是VIP用户，直接下载当前选中的内容
  emit('download', {
    logo: currentLogo.value,
    exampleIndex: currentIndex.value,
    previewImage: currentPreviewImage.value,
    originalSvg: originalSvgContent.value,
    isOriginalSvg: currentIndex.value === 0
  })
}

const handleDownload = () => {
  emit('download', {
    logo: currentLogo.value,
    exampleIndex: currentIndex.value,
    previewImage: currentPreviewImage.value,
    originalSvg: originalSvgContent.value,
    isOriginalSvg: currentIndex.value === 0
  })
}

const handleImageError = (event) => {
  console.error('缩略图加载失败:', event.target.src)
  event.target.style.display = 'none'
}

// 删除SVG中的背景元素
const removeBackgroundElements = (svgContent) => {
  try {
    console.log('开始删除背景元素，原始SVG:', svgContent.substring(0, 200))
    
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml')
    const svgElement = svgDoc.querySelector('svg')
    
    if (!svgElement) {
      console.warn('无效的SVG内容')
      return svgContent
    }
    
    let removedCount = 0
    
    // 查找所有可能的背景元素
    const allRects = svgDoc.querySelectorAll('rect')
    
    allRects.forEach((rect, index) => {
      const x = parseFloat(rect.getAttribute('x') || '0')
      const y = parseFloat(rect.getAttribute('y') || '0')
      const width = rect.getAttribute('width') || ''
      const height = rect.getAttribute('height') || ''
      const id = rect.getAttribute('id') || ''
      const fill = rect.getAttribute('fill') || ''
      
      // 判断是否为背景矩形的条件：
      // 1. ID为bg
      // 2. 位于(0,0)且尺寸为画布大小
      // 3. 宽度或高度为100%
      // 4. 是SVG的直接子元素且位于最前面
      const isBackgroundRect = (
        id === 'bg' ||
        (x === 0 && y === 0 && (
          width === '100%' || height === '100%' ||
          width === '800' || height === '600' ||
          (parseFloat(width) >= 700 && parseFloat(height) >= 500)
        )) ||
        // 检查是否是第一个rect元素（通常背景在最前面）
        (rect === svgElement.querySelector('rect') && x === 0 && y === 0)
      )
      
      if (isBackgroundRect) {
        console.log(`删除背景元素 ${removedCount + 1}:`, {
          tagName: rect.tagName,
          id, x, y, width, height, fill,
          isFirstRect: rect === svgElement.querySelector('rect')
        })
        rect.remove()
        removedCount++
      }
    })
    
    // 清理SVG根元素的背景样式属性
    const svgRoot = svgDoc.querySelector('svg')
    if (svgRoot) {
      // 移除背景相关的style属性
      const style = svgRoot.getAttribute('style')
      if (style) {
        // 移除background-color相关样式
        let cleanedStyle = style
          .replace(/background-color\s*:\s*[^;]+;?/gi, '')
          .replace(/background\s*:\s*[^;]+;?/gi, '')
          .replace(/;\s*;/g, ';') // 清理多余的分号
          .replace(/^;\s*|;\s*$/g, '') // 清理开头和结尾的分号
          .trim()
        
        if (cleanedStyle) {
          svgRoot.setAttribute('style', cleanedStyle)
          console.log('清理后的style属性:', cleanedStyle)
        } else {
          svgRoot.removeAttribute('style')
          console.log('已移除空的style属性')
        }
      }
      
      // 移除其他可能的背景属性
      svgRoot.removeAttribute('bgcolor')
      
      // 移除Vue相关的属性（如果存在）
      svgRoot.removeAttribute('data-v-63f72ae9')
      
      // 移除class属性中的背景相关类名
      const className = svgRoot.getAttribute('class')
      if (className) {
        const cleanedClass = className
          .replace(/\s*svg-canvas\s*/g, ' ')
          .replace(/\s*background\s*/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
        
        if (cleanedClass) {
          svgRoot.setAttribute('class', cleanedClass)
        } else {
          svgRoot.removeAttribute('class')
        }
      }
      
      console.log('✅ SVG根元素背景样式清理完成')
    }
    
    // 序列化回字符串
    const serializer = new XMLSerializer()
    const cleanedSvg = serializer.serializeToString(svgDoc)
    
    console.log(`✅ 背景元素删除完成，共删除 ${removedCount} 个元素`)
    console.log('清理后的SVG:', cleanedSvg.substring(0, 300))
    
    return cleanedSvg
    
  } catch (error) {
    console.error('删除背景元素失败:', error)
    return svgContent // 返回原始内容
  }
}

// SVG 转 Base64 编码
const svgToBase64 = (svgContent) => {
  try {
    // 确保SVG内容是完整的
    let processedSvg = svgContent.trim()
    if (!processedSvg.startsWith('<svg')) {
      console.warn('SVG内容格式可能不正确')
      return null
    }
    
    // 先删除背景元素
    processedSvg = removeBackgroundElements(processedSvg)
    
    // 将SVG转换为Base64
    const base64 = btoa(unescape(encodeURIComponent(processedSvg)))
    return `data:image/svg+xml;base64,${base64}`
  } catch (error) {
    console.error('SVG转Base64失败:', error)
    return null
  }
}

// 通过URL获取SVG内容
const fetchSvgFromUrl = async (url) => {
  try {
    console.log('获取SVG模板:', url)
    
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error(`HTTP错误: ${response.status}`)
    }
    
    const svgContent = await response.text()
    console.log('✅ SVG模板获取成功:', svgContent.substring(0, 100))
    
    return svgContent
  } catch (error) {
    console.error('获取SVG模板失败:', error)
    return null
  }
}

// 处理SVG模板，替换[img/]标签
const processSvgTemplate = async (templateSvgOrUrl, logoSvgContent) => {
  try {
    if (!templateSvgOrUrl || !logoSvgContent) {
      console.warn('模板SVG或Logo SVG内容为空')
      return null
    }
    
    let templateSvg = templateSvgOrUrl
    
    // 检查是否是URL，如果是则先获取SVG内容
    if (typeof templateSvgOrUrl === 'string' && (templateSvgOrUrl.startsWith('http://') || templateSvgOrUrl.startsWith('https://'))) {
      console.log('检测到URL，开始获取SVG模板内容:', templateSvgOrUrl)
      templateSvg = await fetchSvgFromUrl(templateSvgOrUrl)
      
      if (!templateSvg) {
        console.error('无法获取SVG模板内容')
        return null
      }
    }
    
    console.log('处理SVG模板:', { 
      templateSvg: typeof templateSvg === 'string' ? templateSvg.substring(0, 100) : templateSvg, 
      logoSvg: logoSvgContent.substring(0, 100) 
    })
    
    // 将Logo SVG转换为Base64
    const logoBase64 = svgToBase64(logoSvgContent)
    if (!logoBase64) {
      console.error('Logo SVG转Base64失败')
      return null
    }
    
    // 替换模板中的[img/]标签
    let processedTemplate = templateSvg.replace(/\[img\/\]/g, logoBase64)
    
    // 如果没有找到[img/]标签，尝试其他可能的标签格式
    if (processedTemplate === templateSvg) {
      processedTemplate = templateSvg.replace(/\[img\]/g, logoBase64)
    }
    
    if (processedTemplate === templateSvg) {
      console.warn('未找到可替换的[img/]标签，返回原始模板')
    } else {
      console.log('✅ SVG模板处理成功')
    }
    
    return processedTemplate
  } catch (error) {
    console.error('处理SVG模板失败:', error)
    return templateSvgOrUrl // 返回原始内容作为备选
  }
}

// 加载Logo示例和预览图
const loadLogoExamples = async () => {
  if (!currentLogo.value || !currentLogo.value.goods_id) return
  
  try {
    // 开始加载缩略图
    isLoadingThumbnails.value = true
    console.log('调用getExampleApi获取预览图，goods_id:', currentLogo.value.goods_id)
    
    const response = await getExampleApi({
        goods_id: currentLogo.value.goods_id
    })
    
    console.log('getExampleApi响应:', response)
    
    if (response.data && response.data.success) {
      const data = response.data
      
      // 保存Logo详情
      logoDetails.value = data
      
      // 提取预览图片 - 根据API响应结构调整
      if (data.Images && Array.isArray(data.Images)) {
        previewImages.value = data.Images
      } else if (data.examples && Array.isArray(data.examples)) {
        previewImages.value = data.examples
      } else if (data.preview_images && Array.isArray(data.preview_images)) {
        previewImages.value = data.preview_images
      }
      
      console.log('获取到的预览图模板:', previewImages.value)
      
      // 初始化加载状态数组
      thumbnailLoadingStates.value = new Array(previewImages.value.length).fill(true)
      processedPreviewImages.value = new Array(previewImages.value.length).fill(null)
      
      // 处理SVG模板，替换[img/]标签 - 逐张处理
      if (previewImages.value.length > 0 && currentLogo.value.svgData) {
        console.log('开始处理SVG模板，Logo SVG数据:', currentLogo.value.svgData.substring(0, 100))
        
        // 逐张处理预览图，而不是等待所有完成
        previewImages.value.forEach(async (templateSvgOrUrl, index) => {
          try {
            const processedSvg = await processSvgTemplate(templateSvgOrUrl, currentLogo.value.svgData)
            if (processedSvg) {
              // 将处理后的SVG转换为Data URL用于显示
              const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(processedSvg)}`
              console.log(`✅ 预览图${index + 1}处理完成`)
              
              // 更新对应位置的图片和加载状态
              processedPreviewImages.value[index] = svgDataUrl
              thumbnailLoadingStates.value[index] = false
            } else {
              console.warn(`⚠️ 预览图${index + 1}处理失败`)
              thumbnailLoadingStates.value[index] = false
            }
          } catch (error) {
            console.error(`❌ 预览图${index + 1}处理异常:`, error)
            thumbnailLoadingStates.value[index] = false
          }
        })
      } else {
        console.warn('没有预览图模板或Logo SVG数据')
        thumbnailLoadingStates.value = []
        processedPreviewImages.value = []
      }
      
      if (previewImages.value.length === 0) {
        ElMessage.info('暂无预览图')
      }
    } else {
      ElMessage.warning('获取Logo示例失败')
    }
  } catch (error) {
    console.error('获取Logo示例时发生错误:', error)
    ElMessage.error('获取示例失败，请重试')
  } finally {
    // 加载完成，隐藏骨架屏
    isLoadingThumbnails.value = false
  }
}

// 监听器
watch(() => props.visible, (newValue) => {
  if (newValue && props.logo) {
    // 重置状态
    currentIndex.value = 0
    
    // 如果是生成的Logo，调用API获取预览图
    if (props.logo.isGenerated && props.logo.goods_id) {
      nextTick(() => {
        loadLogoExamples()
      })
    }
  }
})

watch(() => props.logo, (newLogo) => {
  if (newLogo && props.visible) {
    // 重置状态
    currentIndex.value = 0
    
    // 如果是生成的Logo，调用API获取预览图
    if (newLogo.isGenerated && newLogo.goods_id) {
      loadLogoExamples()
    }
  }
}, { deep: true })
</script>

<style scoped>
.preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.preview-container {

  border-radius: 20px;
  padding: 40px;
  max-width: 880px;
  width: 90%;
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.preview-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #f5f5f5;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #666;
}

.preview-close-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.preview-logo-area {
  width: 100%;
}

.preview-canvas {
  width: 100%;
  height: 600px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.preview-canvas::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('@/assets/static/images/水印图.png');
  background-repeat: no-repeat;
  background-size: 500px 400px;
  background-position: 180px 100px;
  opacity: 0.7;
  pointer-events: none;
  z-index: 10;
  transform: rotate(45deg) scale(1.6);
}



.preview-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e1e5e9;
  border-top: 3px solid #4285f4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.preview-svg-content,
.preview-image-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}

.preview-svg-content svg,
.preview-image-content img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.preview-no-content {
  color: #999;
  text-align: center;
}

.preview-examples-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.examples-thumbnails {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  max-width: 800px;
  justify-content: center;
}

.examples-label {
  font-size: 14px;
  color: #939292;

  white-space: nowrap;
}

.thumbnail-separator {
  width: 1px;
  height: 30px;
  background: #939292;
  margin: 0 12px;
  flex-shrink: 0;
}

.scene-label {
  display: flex;
  align-items: center;
  margin-right: 8px;
  flex-shrink: 0;
}

.example-thumbnail {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.example-thumbnail:hover {
  transform: scale(1.05);
  border-color: #0066ff;
}

.example-thumbnail.active {
  border-color: #0066ff;
  box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.2);
}

.example-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.original-svg-thumbnail {
  position: relative;
  background: #f8f9fa;
}

.svg-thumbnail-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.svg-thumbnail-content svg {
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
}

.thumbnail-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  text-align: center;
  padding: 2px;
  border-radius: 0 0 4px 4px;
}

/* 骨架屏缩略图样式 */
.skeleton-thumbnail {
  cursor: default;
  pointer-events: none;
}

.skeleton-thumbnail:hover {
  transform: none;
  border-color: transparent;
}

.skeleton-thumbnail-content {
  width: 100%;
  height: 100%;
  position: relative;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}


.preview-actions {
  display: flex;
  gap: 15px;
}

.vip-download-btn {
  background: linear-gradient(135deg, #ff9800, #ff5722);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;

}

.vip-download-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
}

.vip-icon {
  font-size: 16px;
}

.download-logo-btn {
  background: #0066ff;
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

}

.download-logo-btn:hover {
  background: #0052cc;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 102, 255, 0.4);
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .preview-container {
    width: 95%;
    padding: 30px;
    gap: 25px;
  }
  
  .preview-canvas {
    height: 300px;
  }

  .preview-examples-selector {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
  
  .examples-thumbnails {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    max-width: 100%;
  }
  
  .thumbnail-separator {
    width: 30px;
    height: 1px;
    margin: 0;
  }
}

@media (max-width: 768px) {
  .preview-container {
    width: 95%;
    padding: 20px;
    gap: 20px;
  }
  
  .preview-canvas {
    height: 250px;
  }
  
  .preview-actions {
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }
  
  .vip-download-btn,
  .download-logo-btn {
    width: 100%;
    justify-content: center;
  }

  .examples-thumbnails {
    flex-direction: row;
    justify-content: center;
    max-width: 100%;
    flex-wrap: wrap;
  }
  
  .thumbnail-separator {
    width: 20px;
    height: 1px;
    margin: 0 8px;
  }

  .example-thumbnail {
    width: 40px;
    height: 40px;
  }
}
</style>
